export interface NotificationNavigationIntent {
  channelId: string | null;
  messageId: string | null;
  threadId: string | null;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Validate that a value is a safe non-empty identifier (UUID or alphanumeric slug). */
function validateUuid(value: string | null): string | null {
  if (!value) return null;
  // Accept UUIDs and safe alphanumeric slugs (e.g. "ch-1", "msg-abc")
  if (UUID_RE.test(value)) return value;
  if (/^[a-zA-Z0-9_-]{1,128}$/.test(value)) return value;
  return null;
}

function decodePathPart(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function readNotificationNavigationIntent(
  href: string,
): NotificationNavigationIntent {
  const url = new URL(href);
  const channelParam = url.searchParams.get("channel");
  const messageParam = url.searchParams.get("message");
  const threadParam = url.searchParams.get("thread");

  const pathMatch = url.pathname.match(/^\/channel\/([^/]+)$/);
  const channelFromPath = pathMatch?.[1] ? decodePathPart(pathMatch[1]) : null;

  // Validate all IDs are UUIDs to prevent injection of arbitrary values
  const channelId = validateUuid(channelParam) || validateUuid(channelFromPath);
  const messageId = validateUuid(messageParam);
  const threadId = validateUuid(threadParam);

  return { channelId, messageId, threadId };
}

export function stripNotificationNavigationIntent(href: string): string {
  const url = new URL(href);

  url.searchParams.delete("channel");
  url.searchParams.delete("message");
  url.searchParams.delete("thread");

  if (url.pathname.match(/^\/channel\/[^/]+$/)) {
    url.pathname = "/";
  }

  const query = url.searchParams.toString();
  return `${url.pathname}${query ? `?${query}` : ""}${url.hash}`;
}
